import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { generateShades, mapShadesToObject } from '@core/functions/colors';
import { ClientConfig } from '@core/models/client.model';
import {
  ConvertDemoToOfficialResponse,
  Demo,
  DemoRequest,
  DemoRequestWithAssets,
  DemoResponse,
  DemosResponse,
  DemoImageType,
} from '@modules/demo/domain/models/demo.interface';
import { map, Observable } from 'rxjs';
import { CacheLayerService } from '../cache-layer/cache-layer.service';
import { EnvironmentService } from '../environment/environment.service';
import { base64ToFile } from '@modules/shared/domain/utils/image-convert.util';

@Injectable({
  providedIn: 'root',
})
export class DemoService {
  private readonly environment = inject(EnvironmentService);
  private readonly http = inject(HttpClient);
  private readonly cache = inject(CacheLayerService);

  private readonly API_URL_V1 = this.environment.getApiUrl() + 'v1/demo-config/';
  private readonly API_URL_V2 = this.environment.getApiUrl() + 'v2/demo-config/';

  private demosSignal = signal<Demo[]>(
    this.getAssignedDemos().pipe(
      map((demos) => {
        return demos;
      }),
    ) as unknown as Demo[],
  );
  public demos = this.demosSignal.asReadonly() || [];

  private base64ToBlob(dataURI: string): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
  }

  private demoToMultipartFormData(demo: DemoRequestWithAssets): FormData {
    const formData = new FormData();

    Object.keys(demo).forEach((key) => {
      const value = (demo as any)[key];
      if (value === null || value === undefined) return;

      // Si es un string y es un Data URI (Base64), lo convertimos a Blob
      if (typeof value === 'string' && value.startsWith('data:image')) {
        const blob = this.base64ToBlob(value);
        // Es vital ponerle un nombre de archivo (ej: 'image.png')
        // para que el backend lo reconozca como archivo subido
        formData.append(key, blob, `${key}.png`);
      } else if (value instanceof File || value instanceof Blob) {
        formData.append(key, value);
      } else if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
      } else if (typeof value === 'object') {
        // welcome_message y demás objetos viajan como JSON: en multipart no
        // hay campos anidados y String(value) daría "[object Object]".
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });

    return formData;
  }

  getAssignedDemos(subUserId?: number): Observable<DemoResponse[]> {
    const headers = subUserId ? new HttpHeaders({ 'x-sub-userid': String(subUserId) }) : undefined;

    return this.http
      .get<DemosResponse>(`${this.API_URL_V1}assigned-demos`, { headers })
      .pipe(map((response) => response.demos));
  }

  createDemo(client_id: number, demo: DemoRequestWithAssets): Observable<ClientConfig> {
    const newDemoFormData = this.demoToMultipartFormData(demo);
    return this.http.post<ClientConfig>(`${this.API_URL_V2}${client_id}`, newDemoFormData);
  }

  updateDemo(demo_id: number, demo: DemoRequest): Observable<Demo> {
    const formData = new FormData();
    Object.keys(demo).forEach((key) => {
      const value = (demo as any)[key];
      if (value === null || value === undefined) return;

      // Mismo motivo que en la creación: los objetos van serializados.
      formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    });

    return this.http.patch<Demo>(`${this.API_URL_V2}${demo_id}`, formData);
  }

  deleteDemo(demo_id: number): Observable<any> {
    return this.http.delete<any>(`${this.API_URL_V1}${demo_id}`);
  }

  getDemoById(client_id: number, demo_id: number): Observable<DemoResponse> {
    return this.http.get<DemoResponse>(`${this.API_URL_V1}${client_id}/${demo_id}`);
  }

  getDemosByClientId(client_id: number): Observable<DemoResponse[]> {
    return this.http
      .get<DemosResponse>(`${this.API_URL_V1}${client_id}`)
      .pipe(map((response) => response.demos));
  }

  convertDemoToOfficial(demo_id: number): Observable<ConvertDemoToOfficialResponse> {
    return this.http.post<ConvertDemoToOfficialResponse>(`${this.API_URL_V1}${demo_id}/convert`, {
      demo_id,
    });
  }

  /**
   * Subir imagen como multipart/form-data.
   * - `image_type` debe ser uno de los valores permitidos ('logo'|'banner'...)
   * - `image` es el archivo a subir
   */
  uploadImage(
    demo_id: number,
    imageFile: File,
    image_type: DemoImageType,
  ): Observable<DemoResponse> {
    const form = new FormData();
    form.append('image_type', image_type);
    form.append('image', imageFile, imageFile.name);

    // NO establecer 'Content-Type' aquí: el navegador/HttpClient lo asignará con boundary
    return this.http.put<DemoResponse>(`${this.API_URL_V1}upload-image/${demo_id}`, form);
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  base64ToFile(base64: string, filename: string): File {
    return base64ToFile(base64, filename);
  }

  demoResponseToDemo(demoResponse: DemoResponse): Demo {
    return {
      ...demoResponse,
      banner: demoResponse?.banner_url || './images/home-bg.webp',
      logo: demoResponse?.logo_url || './icons/logo-d2d.svg',
      shades: mapShadesToObject(generateShades(demoResponse.primary_color)),
    };
  }
}
